import React, { useEffect, useRef } from "react";
import { Minus, Plus } from "lucide-react";
import { COLORS, SIZES, bn, WC_PRODUCT_IDS, WC_VARIATION_IDS, WC_VARIATION_SKUS } from "@/lib/shop";
import { trackBoth } from "@/lib/meta";

const SIZE_SHORT = ["M", "L", "XL", "XXL"];

export default function ProductSelect({ lines, setLines, stock }) {
    const rootRef = useRef(null);
    const vcTracked = useRef(false);

    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const ob = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !vcTracked.current) {
                    vcTracked.current = true;
                    trackBoth("ViewContent", {
                        value: 900, currency: "BDT",
                                        content_ids: ["11688", "11489", "11517"], content_type: "product",
                                        content_name: "খাদি পাঞ্জাবি",
                                        content_category: "পাঞ্জাবি",
                                        contents: [
                                            { id: "11688", quantity: 1, item_price: 900, color: "ক্রিম" },
                                            { id: "11489", quantity: 1, item_price: 900, color: "সাদা" },
                                            { id: "11517", quantity: 1, item_price: 900, color: "কালো" },
                                        ],
                                    });
                                    ob.disconnect();
                                }
            },
            { threshold: 0.25 },
        );
        ob.observe(el);
        return () => ob.disconnect();
    }, []);

    const totalQty = lines.reduce((a, l) => a + l.qty, 0);

    const addSize = (colorId, sizeId) => {
        if (totalQty >= 10) return;
        const firstForColor = !lines.some((l) => l.color === colorId);
        setLines([...lines, { color: colorId, size: sizeId, qty: 1 }]);
        if (firstForColor) {
                const color = COLORS.find((c) => c.id === colorId);
                        const pid = WC_PRODUCT_IDS[colorId];
                        const vid = WC_VARIATION_IDS[colorId]?.[sizeId];
                        const sku = WC_VARIATION_SKUS[colorId]?.[sizeId];
                        trackBoth("AddToCart", {
                            value: 900,
                            currency: "BDT",
                            content_ids: [String(pid || "khadi-punjabi")],
                            content_type: "product",
                            content_name: `খাদি পাঞ্জাবি — ${color?.name || ""}`,
                            content_category: "পাঞ্জাবি",
                            contents: [{
                                id: String(pid || "khadi-punjabi"),
                                quantity: 1,
                                item_price: 900,
                                variation_id: vid || null,
                                sku: sku || null,
                                color: color?.name || "",
                                size: sizeId,
                            }],
                            num_items: 1,
                        });
                    }
        };

    const changeQty = (colorId, sizeId, d) => {
        setLines(
            lines
                .map((l) => (l.color === colorId && l.size === sizeId ? { ...l, qty: Math.min(5, l.qty + d) } : l))
                .filter((l) => l.qty > 0),
        );
    };

    return (
        <div ref={rootRef} className="grid sm:grid-cols-3 gap-3 sm:gap-4" data-testid="product-select-grid">
            {COLORS.map((c) => {
                const my = lines.filter((l) => l.color === c.id);
                const myQty = my.reduce((a, l) => a + l.qty, 0);
                const unavailable = stock && stock[c.id] && !stock[c.id].available;
                const active = myQty > 0;
                return (
                    <div
                        key={c.id}
                        data-testid={`product-card-${c.id}`}
                        className={`rounded-2xl border-2 bg-white p-2.5 sm:p-4 transition-all duration-200 ${
                            active ? "border-[#A84320] shadow-lg shadow-[#A84320]/10" : "border-[#E4DDD3]"
                        } ${unavailable ? "opacity-60" : ""}`}
                    >
                        {/* ─── Mobile: horizontal header (image left, info right) / Desktop: vertical ─── */}
                                                <div className="flex sm:block gap-3">
                                                    <div className="relative shrink-0">
                                                        <img src={c.img} alt={`${c.name} খাদি পাঞ্জাবি`} loading="lazy"
                                                                                                                    className="w-24 h-24 sm:w-full sm:aspect-[4/3] sm:h-auto object-cover rounded-xl" />
                                                        {unavailable && (
                                                            <span className="absolute inset-0 rounded-xl bg-[#1A1715]/70 flex items-center justify-center text-sm font-bold text-[#F4EFE6]" data-testid={`stock-out-${c.id}`}>
                                                                স্টক শেষ
                                                            </span>
                                                        )}
                                                        {active && !unavailable && (
                                                            <span className="absolute top-2 right-2 bg-[#A84320] text-[#FBF9F5] text-xs font-bold rounded-full px-2.5 py-1" data-testid={`selected-count-${c.id}`}>
                                                                {bn(myQty)}টি
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0 sm:mt-3">
                                                        <div className="flex items-baseline justify-between sm:block">
                                                            <p className="font-bold text-[#1E1B18]">{c.name}</p>
                                                            <p className="flex items-baseline gap-1.5 sm:mt-1">
                                                                <span className="text-xs text-[#8A8278] line-through">৳১,৩০০</span>
                                                                <span className="text-base font-extrabold text-[#9E3B1B]">৳৯০০</span>
                                                            </p>
                                                        </div>
                                                        <p className="text-[11px] text-[#8A8278] mt-2 mb-1">সাইজে ট্যাপ করুন</p>
                                                        <div className="grid grid-cols-4 gap-1.5">
                                                            {SIZES.map((s, si) => {
                                                                const line = my.find((l) => l.size === s.id);
                                                                return (
                                                                    <button
                                                                        type="button"
                                                                        key={s.id}
                                                                        disabled={unavailable}
                                                                        onClick={() => (line ? changeQty(c.id, s.id, -line.qty) : addSize(c.id, s.id))}
                                                                        data-testid={`size-${c.id}-${s.id}`}
                                                                        aria-pressed={!!line}
                                                                        className={`rounded-lg border-2 py-1 text-center transition-all duration-150 ${
                                                                            line
                                                                                ? "border-[#A84320] bg-[#A84320] text-[#FBF9F5]"
                                                                                : "border-[#E4DDD3] text-[#4A453F] hover:border-[#C9B896]"
                                                                        } ${unavailable ? "cursor-not-allowed" : ""}`}
                                                                    >
                                                                        <span className="block text-[13px] font-bold leading-none">{s.id}</span>
                                                                        <span className={`block text-[9px] mt-0.5 ${line ? "text-[#F4D9CC]" : "text-[#8A8278]"}`}>{SIZE_SHORT[si]}</span>
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* ─── Inline steppers: single compact row right below size chips ─── */}
                                                {my.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`qty-rows-${c.id}`}>
                                                        {my.map((l) => (
                                                                                                                    <div key={l.size} className="flex items-center gap-1 bg-[#FBF9F5] border border-[#E4DDD3] rounded-full py-0.5 pl-2 pr-0.5">
                                                                                                                        <span className="text-[11px] sm:text-xs font-semibold text-[#1E1B18]">সাইজ {l.size}</span>
                                                                <button type="button" onClick={() => changeQty(c.id, l.size, -1)}
                                                                    data-testid={`qty-decrease-${c.id}-${l.size}`}
                                                                                                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-[#E4DDD3] flex items-center justify-center hover:border-[#A84320] transition-colors"
                                                                    aria-label="কমান">
                                                                                                                            <Minus size={12} />
                                                                </button>
                                                                                                                        <span className="text-[13px] font-bold min-w-4 text-center" data-testid={`qty-${c.id}-${l.size}`}>{bn(l.qty)}</span>
                                                                <button type="button" onClick={() => changeQty(c.id, l.size, 1)} disabled={l.qty >= 5}
                                                                    data-testid={`qty-increase-${c.id}-${l.size}`}
                                                                                                                            className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white border border-[#E4DDD3] flex items-center justify-center disabled:opacity-35 hover:border-[#A84320] transition-colors"
                                                                    aria-label="বাড়ান">
                                                                                                                            <Plus size={12} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        }
